import _ from 'lodash'
import CustomTableUtilMixin from '@/mixins/custom-table-util-mixin'
import TransferDom from '@/directives/transfer-dom'

export default {
  mixins: [
    CustomTableUtilMixin
  ],

  directives: {
    TransferDom
  },

  props: {
    aggregationColumn: String,

    columns: {
      type: Object,
      default: () => {}
    },

    data: {
      type: Array,
      default: () => []
    },

    loading: {
      type: Boolean,
      default: false
    },

    tableType: {
      type: String,
      enum: ['vertical', 'horizontal'],
      default: 'vertical'
    },

    headerColumn: {
      type: Object,
      required: false
    },

    additionalHeaderColumn: {
      type: Object,
      required: false
    },

    additionalColumns: {
      type: Array,
      required: false,
      default: () => []
    },

    groupBy: {
      type: String,
      required: false
    },

    border: {
      type: Boolean,
      default: false
    },

    stripe: {
      type: Boolean,
      default: false
    },

    scroll: {
      type: Boolean,
      default: false
    },

    noDataText: {
      type: String,
      default: 'No results'
    },

    maxHeight: {
      type: Number,
      default: 0
    },

    chart: {
      type: Object,
      default: () => {}
    },

    styles: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      computedColumns: [],
      copyOfComputedData: [],
      tableHeaderAdditionalLeftColWidth: _.get(this.styles, 'additionalLeftColWidth', 30),
      tableHeaderLeftColWidth: _.get(this.styles, 'leftColWidth', 200),
      tableHeaderRightColWidth: _.get(this.styles, 'rightColWidth', 150),
      tableColMinWidth: _.get(this.styles, 'colMinWidth', 85),
      tableWidth: 0,
      tableHeight: 0,
      selectedRow: {},
      selectedColumn: '',
      showTooltip: false,
      tooltipTop: 0,
      tooltipLeft: 0,
      numberTypes: ['number', 'decimal', 'percentage', 'currency', 'currencyDecimal'],
      dateTypes: ['date']
    }
  },

  computed: {
    computedData () {
      return _.chain(this.data)
        .groupBy(this.additionalHeaderColumn.name)
        .map((value, key) => {
          const obj = {}
          obj[this.additionalHeaderColumn.name] = key
          value = value.reduce((acc, cur) => {
            const key = cur[this.headerColumn.name]
            acc[key] = cur
            return acc
          }, {})
          return { ...obj, ...value }
        })
        .value()
    },

    tableColWidth () {
      const tableHeaderAdditionalLeftColWidth = this.additionalColumns.length ? this.tableHeaderAdditionalLeftColWidth : 0
      const tableHeaderRightColWidth = this.chart ? this.tableHeaderRightColWidth : 0
      const width = (this.tableWidth - this.tableHeaderLeftColWidth - tableHeaderAdditionalLeftColWidth - tableHeaderRightColWidth) / this.tableColNum

      return width < this.tableColMinWidth ? this.tableColMinWidth : width
    },

    tableHeaderRow () {
      if (!this.data.length) {
        return []
      }
      return this.data.map(row => row[this.headerColumn.name])
        .reduce((acc, cur) => acc.includes(cur) ? acc : [...acc, cur], [])
    },

    hasTooltip () {
      return _.has(this.columns, [this.aggregationColumn, 'tooltip'], false)
    },

    /**
     * Util function to check chart is needed
     * @return {Boolean}
     */
    hasChart () {
      return !_.isEmpty(this.chart)
    }
  },

  watch: {
    computedData (val) {
      this.copyOfComputedData = val
    }
  },

  methods: {
    /**
     * Compute columns for display
     * @return {Array} columns
     */
    computeColumns () {
      const columns = _.chain(this.data)
        .groupBy(this.headerColumn.name)
        .map((value, key) => {
          return key
        })
        .value()
      return columns.reduce((acc, cur) => {
        acc[cur] = _.cloneDeep(this.headerColumn)
        return acc
      }, {})
    },

    /**
     * Compute Line Chart data for display
     * @param {Array}  dataset
     * @param {String} xColumn
     * @param {String} yColumn
     * @return {Array} line chart data for column
     */
    computeLineChartData (dataset, xColumn, yColumn) {
      const xValues = dataset.map(row => row[xColumn])
      const yValues = dataset.map(row => row[yColumn])

      if (xValues.length) {
        xValues.pop()
      }
      if (yValues.length) {
        yValues.pop()
      }

      return Object.freeze({ x: xValues, y: yValues })
    },

    /**
     * Change order type
     * @param {String} column Column name for sorting
     * @param {String} orderType Column order type, can be 'normal'(unordered), 'asc' or 'desc'
     * @param {String} columnType can be column or header
     */
    handleSort (column, orderType, columnType = 'column') {
      Object.keys(this.computedColumns).forEach(column => (this.computedColumns[column].orderType = 'normal'))
      this.additionalHeaderColumn.orderType = 'normal'
      columnType === 'column' ? this.computedColumns[column].orderType = orderType : this.additionalHeaderColumn.orderType = orderType
      const orderedData = orderType === 'normal' ? this.computedData
        : columnType === 'column' ? _.orderBy(this.computedData, [(o) => _.get(o, [column, this.aggregationColumn], '')], [orderType])
          : _.orderBy(this.computedData, [column], orderType)
      this.copyOfComputedData = orderedData
    },

    /**
     * Change column order type when user click on head
     * @param {String} column Selected column for sorting
     * @param {String} columnType can be column or header
     */
    handleSortByHead (column, columnType = 'column') {
      const orderType = columnType === 'column' ? this.computedColumns[column].orderType : this.additionalHeaderColumn.orderType
      if (orderType === 'normal') {
        this.handleSort(column, 'desc', columnType)
      } else if (orderType === 'desc') {
        this.handleSort(column, 'asc', columnType)
      } else {
        this.handleSort(column, 'normal', columnType)
      }
    },

    /**
     * Check order type param existance within column
     * @param {String} column
     * @return {Boolean} if orderType param exists in columns
     */
    checkOrderType (column) {
      return _.has(this.computedColumns, [column, 'orderType'])
    },

    /**
     * Format cell based on path and format
     * @param {Array} path The path of the property to get.
     * @param {String} format
     * @param {String} fromFormat Original date format
     * @param {String} toFormat Transformed date format
     * @param {Number} precision Precision for number casting
     * @return {Number|Date|String} return formatted value based on types
     */
    formatCellByPath (path, format, fromFormat, toFormat, precision = 2) {
      if (this.numberTypes.includes(format)) {
        return this.formatNumber(_.get(this.selectedRow, path, ''), format, precision)
      } else if (this.dateTypes.includes(format)) {
        return this.$moment(_.get(this.selectedRow, path, new Date()), fromFormat).format(toFormat)
      } else {
        return _.get(this.selectedRow, path, '')
      }
    },

    /**
     * Format value based on path and format
     * @param {String} value
     * @param {String} format
     * @param {String} fromFormat Original date format
     * @param {String} toFormat Transformed date format
     * @param {Number} precision Precision for number casting
     * @return {Number|Date|String} return formatted value based on types
     */
    formatCellByValue (value, format, fromFormat, toFormat, precision = 0) {
      if (this.numberTypes.includes(format)) {
        return this.formatNumber(value || '', format, precision)
      } else if (this.dateTypes.includes(format)) {
        return this.$moment(value || new Date(), fromFormat).format(toFormat)
      } else {
        return value
      }
    }
  }
}
